import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import './ConfirmSubscriptionModal.css';

interface ConfirmSubscriptionModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}

const ConfirmSubscriptionModal: React.FC<ConfirmSubscriptionModalProps> = ({
  isOpen,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleConfirm = async () => {
    if (isLoading) {
      return;
    }
    try {
      setIsLoading(true);
      await Promise.resolve(onConfirm());
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isLoading) {
      return;
    }
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="subscription-modal-overlay" onClick={handleOverlayClick}>
      <div className="subscription-modal-container" role="dialog" aria-modal="true">
        <button
          type="button"
          className="subscription-modal-close"
          onClick={onClose}
          aria-label="Close"
          disabled={isLoading}
        >
          <X size={20} />
        </button>

        <div className="subscription-modal-content">
          <h2 className="subscription-modal-title">{title}</h2>
          {description && <p className="subscription-modal-description">{description}</p>}
        </div>

        <div className="subscription-modal-actions">
          <button
            type="button"
            className="subscription-modal-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`subscription-modal-primary${
              tone === 'danger' ? ' subscription-modal-primary--danger' : ''
            }`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSubscriptionModal;
